// ** React Imports
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useDispatch } from "react-redux";
import { getRankings } from "store/modules/main-pages/rankings/get-ranking.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import loadable from "@loadable/component";

// ** Custom Components
import Heading from "components/typography/Heading";
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import EmptyBlock from "components/layout/Block/BlockEmpty";
import ScrollToTop from "components/global/ScrollToTop";

// ** Ranking components
const RankingGrid = loadable(() => import("./components/RankingGrid"));
const FiltersSection = loadable(() => import("./components/FiltersSection"));
const NavPanel = loadable(() => import("./components/NavPanel"));

// ** Custom Hooks
import useRankingsMetaTags from "./hooks/useRankingsMetaTags";

// ** Hooks
import useRankingsValidator from "./hooks/useRankingsValidator";
import useRankingsApiParams from "./hooks/useRankingsApiParams";

// #####################################################

const Rankings = () => {
	const { t } = useTranslation([
		"pages",
		"common",
		"countries",
		"regions",
		"filtersAndSelectors",
	]);

	const history = useHistory();

	const dispatch = useDispatch();

	const location = useLocation();

	const [filtersOpen, setFiltersOpen] = useState(false);

	const { title, metaDescription, ogMetaTags, canonicalLink } =
		useRankingsMetaTags();

	const { containInvalidParam } = useRankingsValidator();

	const { getApiParams } = useRankingsApiParams();

	const getRankingsUsersFn = () => {
		const apiParams = getApiParams();
		dispatch(getRankings({ apiParams, useUrl: true }));
	};

	useEffect(() => {
		if (!location.state?.forceReload) {
			setFiltersOpen(false);
			getRankingsUsersFn();
		}
	}, [location.pathname]);

	useEffect(() => {
		if (location.state?.forceReload) {
			history.replace({ ...location, state: null });
			getRankingsUsersFn();
		}
	}, [location.state?.forceReload]);

	// #####################################################

	// ** Przekierowanie na stronę błędu w przypadku nieprawidłowych parametrów w adresie strony
	if (containInvalidParam()) {
		history.replace("/error404");
		return null;
	}

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title}>
				<meta name="description" content={metaDescription} />
				{ogMetaTags}
				{canonicalLink()}
			</Helmet>

			<ScrollToTop />

			<CardsBlockWrapper>
				{/* Filtry */}
				<CardWrapper>
					<CardTitle>
						<Heading type="h2" data-testid="rankings-heading">
							{t(`pages:rankings`)}
						</Heading>
					</CardTitle>
					<CardBody>
						<FiltersSection
							filtersOpen={filtersOpen}
							setFiltersOpen={setFiltersOpen}
						/>
						<NavPanel />
					</CardBody>
				</CardWrapper>

				{/* Główna zawartość strony */}
				<CardWrapper>
					<CardBody>
						<RankingGrid />
					</CardBody>
				</CardWrapper>

				<EmptyBlock />
			</CardsBlockWrapper>
		</>
	);
};

// #####################################################

export default Rankings;

// #####################################################
